import { Order } from './orders';

/**
 * Ermittelt, welche Felder eines Auftrags sich gegenüber dem zuletzt bestätigten
 * Serverstand geändert haben.
 *
 * Hintergrund: Bisher wurde bei jedem Speichern das VOLLSTÄNDIGE Dokument gesendet.
 * Dadurch war ein leeres Feld mehrdeutig — es konnte "vom Nutzer geleert" oder "in
 * dieser Kopie nie gefüllt" bedeuten. Genau diese Mehrdeutigkeit hat in Produktion
 * Produktlisten und Kundendaten gekostet: Eine veraltete Kopie überschrieb gute Daten
 * mit ihren eigenen Leerwerten.
 *
 * Mit Teil-Updates verschwindet das Problem: Was nicht gesendet wird, wird nicht
 * angefasst. Was gesendet wird, ist eine bewusste Änderung — auch ein leerer Wert.
 *
 * WICHTIG: Das betrifft ausschließlich die Übertragung. Lokal wird immer der komplette
 * Auftrag gespeichert, niemals nur die Teiländerungen.
 */

/** Felder, die der Server verwaltet und die nie vom Client geschrieben werden. */
const SERVER_OWNED = new Set<string>([
  'id',
  '_id',
  'Id',
  'revision',
  'createdAt',
  'updatedAt',
  'updatedBy',
  'karmaNoteRef',
  'sync',
]);

/** Felder, die immer mitgesendet werden müssen, damit der Server den Auftrag zuordnen kann. */
const ALWAYS_INCLUDED = ['orderId', 'orderNumber', 'updated'] as const;

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([l], [r]) => l.localeCompare(r))
    .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
  return `{${entries.join(',')}}`;
};

export type OrderDiff = {
  /** Nur die geänderten Felder plus die zur Zuordnung nötigen. */
  payload: Record<string, unknown>;
  /** Namen der tatsächlich geänderten Felder — leer, wenn nichts zu senden ist. */
  changedFields: string[];
};

/**
 * Baut den zu sendenden Teil-Payload.
 *
 * @param current  Der vollständige lokale Auftrag (bleibt unverändert).
 * @param baseline Der zuletzt vom Server bestätigte Stand. Fehlt er, wird alles gesendet
 *                 (Neuanlage oder erster Sync nach dem Update).
 */
export const buildOrderDiff = (current: Order, baseline: Order | undefined): OrderDiff => {
  const currentRecord = current as unknown as Record<string, unknown>;

  // Ohne Vergleichsbasis das komplette Dokument senden — sicherer Ausgangszustand.
  if (!baseline) {
    const full: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(currentRecord)) {
      if (SERVER_OWNED.has(key)) continue;
      full[key] = value;
    }
    return { payload: full, changedFields: Object.keys(full) };
  }

  const baselineRecord = baseline as unknown as Record<string, unknown>;
  const payload: Record<string, unknown> = {};
  const changedFields: string[] = [];

  const keys = new Set([...Object.keys(currentRecord), ...Object.keys(baselineRecord)]);

  for (const key of keys) {
    if (SERVER_OWNED.has(key)) continue;

    const currentValue = currentRecord[key];
    const baselineValue = baselineRecord[key];

    if (stableStringify(currentValue) === stableStringify(baselineValue)) continue;

    // ABSOLUTE REGEL: Produkte dürfen nie von einer gefüllten Liste auf leer gehen.
    // Ein solcher Zustand entsteht ausschließlich durch einen Fehler (veraltete Kopie,
    // unvollständig geladener Auftrag) — ein bewusstes Leeren erfolgt manuell am
    // Datensatz, nicht über die App. Die Änderung wird deshalb gar nicht erst gesendet.
    if (key === 'products') {
      const currentEmpty = !Array.isArray(currentValue) || currentValue.length === 0;
      const baselineFilled = Array.isArray(baselineValue) && baselineValue.length > 0;
      if (currentEmpty && baselineFilled) {
        console.error(
          `[buildOrderDiff] Leerung von "products" verhindert — Auftrag ${current.orderNumber ?? current.orderId}: ` +
          `lokal ${Array.isArray(currentValue) ? currentValue.length : 'null'}, ` +
          `zuletzt bestätigt ${baselineValue.length}. Änderung wird nicht gesendet.`,
        );
        continue;
      }
    }

    payload[key] = currentValue ?? null;
    changedFields.push(key);
  }

  // Zuordnungsfelder immer mitgeben, auch wenn unverändert.
  for (const key of ALWAYS_INCLUDED) {
    if (!(key in payload) && currentRecord[key] !== undefined) {
      payload[key] = currentRecord[key];
    }
  }

  return { payload, changedFields };
};
